import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  Download,
  FileSpreadsheet,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { fencedBlocks, useCodeBlockRenderers } from "./Chat/codeBlockRenderers";
import { useNavigate } from "react-router-dom";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import {
  containerHasTables,
  exportMessageTablesToExcel,
} from "../utils/chatExcelExport";
import { apiFetch } from "../utils/apiClient";
import { getApiBaseURLFromEnv } from "../utils/env";
import { katexOptions, normalizeMathDelimiters } from "../utils/mathMarkdown";
import { exportMessageToPdf } from "../utils/messageExport";
import { pickThinkingMessage } from "../utils/thinkingMessages";
import {
  TRACE_UI_ENABLED,
  isAuditTimelineEnabled,
  isCabinetUiEnabled,
  isCitationArtifactsEnabled,
  isCitationHarnessEnabled,
} from "../utils/featureFlags";
import { contextGraphMessageLink } from "../utils/contextGraphLink";
import { routesNarration, selectAnswerBlocks } from "../utils/auditTimeline";
import { isDopplerUri } from "../utils/dopplerUri";
import { EvidenceEventSchema, type EvidenceEvent } from "../types/evidence";
import { resolveCitation } from "../utils/citationAnchors";
import CitationAnchor from "./CitationChain/CitationAnchor";
import { useCitationChain } from "./CitationChain/CitationChainContext";
import { useDopplerUriOpener } from "../hooks/useDopplerUriOpener";
import { StatusCell } from "./StatusPill";
import "./AssistantMessage.css";
import {
  BlockType,
  ChartBlock as ChartBlockType,
  type ChartOverflowBlock,
  type ChartOverflowPendingBlock,
  type CitationHarnessReportBlock,
  type CitedTableArtifactBlock,
  type LoanTapePreviewBlock,
  type LoanTapePreviewColumn,
  type ModifiedWorkbookBlock,
  type SchemaGraphBlock,
  type TableOverflowBlock,
  type TableOverflowPendingBlock,
  TextBlock,
  TraceBlock,
  TraceType,
  UIMessage,
  UIMessageVersions,
} from "./Chat/Chat.types";
import { steppedVersionId } from "./Chat/Chat.utils";
import ModifiedWorkbookBlockView from "./ModifiedWorkbookBlockView";
import SchemaMappingBlockView from "./SchemaMappingBlockView";
import LoanTapePreviewBlockView from "./LoanTapePreviewBlockView";
import CitedTableArtifactView from "./CitedTableArtifactView";
import CitationHarnessReportView from "./CitationHarnessReportView";
import { resolveCitedTableRender } from "../utils/citationArtifact";
import { tagNumericColumns } from "../utils/markdownTableNumerics";
import { getSessionToken } from "../utils/sessionToken";

const EChartsBlock = lazy(() => import("./EChartsBlock"));

/**
 * A markdown table, with its numeric columns right-aligned in tabular figures.
 *
 * GFM only carries column alignment when the author writes `|---:|`, and the
 * model does not, so every answer table arrived left-aligned in proportional
 * figures — `$90.0M` above `$94.675M` with the decimals out of line. The
 * detection runs against the rendered DOM (see `tagNumericColumns`) because a
 * cell's contents can be wrapped in `<strong>` or split by a citation anchor,
 * and `textContent` is the only reading that survives every shape.
 *
 * A layout effect with no dependency array re-tags on each render, which is
 * what a streaming table needs: the column that looks numeric after two rows
 * may not be after ten.
 */
const MarkdownTable: React.FC<React.TableHTMLAttributes<HTMLTableElement>> = (
  props,
) => {
  const ref = useRef<HTMLTableElement | null>(null);
  useLayoutEffect(() => {
    tagNumericColumns(ref.current);
  });
  return (
    <div className="markdown-table-wrapper">
      <table ref={ref} {...props} />
    </div>
  );
};

// react-markdown's default urlTransform strips non-allowlisted schemes
// (it only permits http/https/mailto/tel/etc.), which would silently
// drop our `doppler://` citation hrefs and break click-to-open. Preserve
// `doppler://` and defer everything else to the default sanitizer.
const dopplerUrlTransform = (url: string): string =>
  url.startsWith("doppler://") ? url : defaultUrlTransform(url);

// ---------------------------------------------------------------------------
// MarkdownErrorBoundary — catch KaTeX / remark crashes, show raw text
// ---------------------------------------------------------------------------

class MarkdownErrorBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// ---------------------------------------------------------------------------
// ThinkingContent — chip that defers to the TracePanel for the actual body.
// The raw reasoning markdown lives in the TracePanel's accordion (one place
// to inspect internals); inline rendering here would duplicate that surface
// and put unfiltered model thoughts into the customer answer flow.
// ---------------------------------------------------------------------------

const ThinkingContent: React.FC<{
  isStreaming: boolean;
  text: string;
}> = ({ isStreaming, text }) => {
  const [expanded, setExpanded] = useState(false);
  const label = isStreaming ? "Thinking..." : "Thinking Step";
  const hasText = text.trim().length > 0;
  return (
    <div className="thinking-block">
      <button
        className="thinking-block-toggle"
        onClick={() => setExpanded((v) => !v)}
        disabled={!hasText}
        aria-expanded={expanded}
      >
        <ChevronRight aria-hidden="true" />
        {label}
      </button>
      {expanded && hasText && (
        <div className="thinking-block-content">{text}</div>
      )}
    </div>
  );
};

const formatBytes = (sizeBytes: number): string => {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const parseFilenameFromDisposition = (
  contentDisposition: string | null,
): string | null => {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }
  const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
};

/** Shown on an answer the pipeline failed to produce. Wording is a product decision; keep it verbatim. */
export const FAILED_ANSWER_NOTICE =
  "Failed to generate or failed to answer. Please press retry.";

/**
 * ``‹ 1 / 2 ›`` over the versions of one question (DOP-436). Stepping selects
 * a version by its persisted id; the page decides what the thread looks like
 * with that version chosen. The newest version is the one the chat continues
 * from, so it is where the switcher rests when nothing is selected.
 */
const AnswerVersionSwitcher: React.FC<{
  versions: UIMessageVersions;
  selectedVersionId?: number;
  onSelectVersion?: (versionMessageId: number) => void;
}> = ({ versions, selectedVersionId, onSelectVersion }) => {
  const older = steppedVersionId(versions, selectedVersionId, -1);
  const newer = steppedVersionId(versions, selectedVersionId, 1);
  return (
    <div
      data-testid="answer-version-switcher"
      className="flex items-center gap-1 text-xs text-gray-600 select-none"
    >
      <button
        type="button"
        aria-label="Previous version"
        disabled={older === undefined || !onSelectVersion}
        onClick={() => older !== undefined && onSelectVersion?.(older)}
        className="px-1 disabled:opacity-30 hover:text-gray-800"
      >
        ‹
      </button>
      <span aria-label={`Version ${versions.index} of ${versions.total}`}>
        {versions.index} / {versions.total}
      </span>
      <button
        type="button"
        aria-label="Next version"
        disabled={newer === undefined || !onSelectVersion}
        onClick={() => newer !== undefined && onSelectVersion?.(newer)}
        className="px-1 disabled:opacity-30 hover:text-gray-800"
      >
        ›
      </button>
    </div>
  );
};

export interface AssistantMessageProps {
  msg: UIMessage;
  isStreamingThis: boolean;
  copiedMessageId: string | null;
  onCopy: (text: string, messageId: string) => void;
  extractText: (msg: UIMessage) => string;
  chatId: string | null;
  /** The chat's arm; narration routing applies only to `claude_code`. */
  chatType?: string | null;
  onOpenTrace?: (msgId: string) => void;
  onOpenEvidence?: (msgId: string) => void;
  /**
   * Open this answer's context graph WHERE THE HOST WANTS IT (DOP-492).
   * The Analyst rail swaps its own body for the graph; with no handler the
   * pill navigates to `/context-graph`, which is right on the chat route and
   * wrong in a rail beside a dashboard the reader has not finished reading.
   */
  onOpenGraph?: (msgId: string) => void;
  /**
   * Which of this answer's views the host has open, if any. The pill that
   * opened it renders in its open state, so on return the reader can see
   * where they have been.
   */
  openView?: "audit" | "graph" | null;
  /**
   * Regenerate this answer in place (DOP-436): a further version of the same
   * question, the old one kept as a readable version. Offered on every
   * answer, finished or failed, and only while nothing else is streaming — the page
   * passes `undefined` otherwise.
   */
  onRegenerate?: () => void;
  /** Which version of this answer's question the reader is looking at. */
  selectedVersionId?: number;
  /** Step to another version of the same question (older/newer). */
  onSelectVersion?: (versionMessageId: number) => void;
  /** Number of column-lineage inspections logged for this message (session-only). */
  inspectionCount?: number;
  /** Append a column-header click to this message's Audit Log series. */
  onInspectColumn?: (column: LoanTapePreviewColumn) => void;
}

const AssistantMessage: React.FC<AssistantMessageProps> = ({
  msg,
  isStreamingThis,
  copiedMessageId,
  onCopy,
  extractText,
  chatId,
  chatType,
  onOpenTrace,
  onOpenEvidence,
  onOpenGraph,
  openView = null,
  onRegenerate,
  selectedVersionId,
  onSelectVersion,
  inspectionCount = 0,
  onInspectColumn,
}) => {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasTables, setHasTables] = useState(false);
  const openDopplerHref = useDopplerUriOpener();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [loadingArtifactId, setLoadingArtifactId] = useState<number | null>(
    null,
  );

  // The bubble renders the *answer*, not everything the model said. With
  // the audit timeline on, interim narration is routed to the timeline
  // lane and only the trailing text run stays here (see
  // `selectAnswerBlocks` for the streaming caveat). With the flag off this
  // is `msg.content_blocks` unchanged.
  const answerBlocks = useMemo(
    () =>
      isAuditTimelineEnabled()
        ? selectAnswerBlocks(msg.content_blocks, {
            isStreaming: isStreamingThis,
            chatType,
          })
        : msg.content_blocks,
    [msg.content_blocks, isStreamingThis, chatType],
  );

  const hasTextContent = answerBlocks.some(
    (b): b is TextBlock => b.type === BlockType.TEXT && b.text.length > 0,
  );

  const traceBlocks = msg.content_blocks.filter(
    (b): b is TraceBlock => b.type === BlockType.TRACE,
  );
  const hasTraceBlocks = traceBlocks.length > 0;

  // The parent (Chat page) owns the EvidencePanel mount and parsing.
  // Kept raw (not parsed) so the pill's count and the graph control stay
  // cheap; `citationEvents` below is the one place that validates them.
  // Prefer the persisted metadata payload; fall back to the live trace block.
  const evidenceEvents = useMemo((): unknown[] => {
    let raw: unknown = msg.evidence?.events;
    if (!Array.isArray(raw)) {
      const evidenceBlock = traceBlocks.find(
        (b) => b.trace_type === TraceType.EVIDENCE_METADATA,
      );
      raw = evidenceBlock?.evidence?.events;
    }
    return Array.isArray(raw) ? raw : [];
  }, [msg.evidence, traceBlocks]);
  // The Audit Log's own count is flag-gated; the evidence itself is not, and
  // the graph control below reads the evidence rather than the pill.
  const evidenceCount = TRACE_UI_ENABLED ? evidenceEvents.length : 0;
  const hasEvidence = evidenceCount > 0;

  /** The Audit log pill: any step, evidence, an inspection, or a live stream —
   *  and somewhere for it to open. A host with no Audit Log surface (the
   *  dashboard's Analyst rail) passes no handler, and a pill that opens
   *  nothing is a broken control, not a disabled one. */
  const showTracePill =
    TRACE_UI_ENABLED &&
    onOpenTrace !== undefined &&
    (hasTraceBlocks || hasEvidence || isStreamingThis || inspectionCount > 0);

  // "Graph" rides on the answer's evidence, like the Audit log pill — but on the
  // RAW count, not the flag-gated one just above: the Audit Log surface is
  // behind `TRACE_UI_ENABLED` and the graph is not, so an answer can offer its
  // neighbourhood on a deployment where the audit log is switched off.
  // the graph behind an answer is built from that answer's verified
  // citations, so an answer with no evidence has no neighbourhood to show.
  const contextGraphHref = useMemo(
    () =>
      contextGraphMessageLink({
        enabled: isCabinetUiEnabled(),
        chatId,
        messageId: msg.id,
        evidenceCount: evidenceEvents.length,
        isStreaming: isStreamingThis,
      }),
    [chatId, msg.id, evidenceEvents.length, isStreamingThis],
  );

  // Parsed here as well as in the Chat page, and deliberately: the anchor
  // binding is per-MESSAGE (an anchor in this bubble must resolve against
  // this bubble's evidence), while the panel's copy is per-ACTIVE-message.
  // Sharing one would silently bind an anchor in an older answer to whatever
  // message the panel happens to be showing.
  const citationChain = useCitationChain();
  const citationEvents = useMemo((): EvidenceEvent[] => {
    if (!TRACE_UI_ENABLED || !citationChain) return [];
    const parsed: EvidenceEvent[] = [];
    for (const item of evidenceEvents) {
      const result = EvidenceEventSchema.safeParse(item);
      if (result.success) parsed.push(result.data);
    }
    return parsed;
  }, [evidenceEvents, citationChain]);
  const coverageStatus = useMemo(() => {
    const metadataStatus = msg.evidence?.coverage_status;
    if (typeof metadataStatus === "string") return metadataStatus;
    const evidenceBlock = traceBlocks.find(
      (b) => b.trace_type === TraceType.EVIDENCE_METADATA,
    );
    const traceStatus = evidenceBlock?.evidence?.coverage_status;
    return typeof traceStatus === "string" ? traceStatus : null;
  }, [msg.evidence, traceBlocks]);
  const coverageWarningText =
    coverageStatus === "failed"
      ? "Citations missing for sourced claims."
      : coverageStatus === "partial"
        ? "Some citations could not be verified."
        : null;
  /*
   * Count the steps the panel will LIST, not the raw blocks behind them.
   *
   * The pill used to filter trace blocks itself while the panel counted
   * collapsed timeline items with narration removed, so the same message
   * read "Audit log · 29 steps" closed and "25 steps" open. Both now go
   * through `auditLogCounts`, which is also what the open drawer's header
   * badge renders. When the timeline is off there is no collapsing to
   * account for and the original block filter is still right.
   */
  // Gated on the FLAG only. Gating it on `routesNarration` as well made the
  // pill fall back to counting raw trace blocks whenever the chat's arm was
  // unknown (a freshly created chat never loads `chat_type`), while the panel
  // With the audit timeline on the bubble is empty for the whole working
  // phase — interim prose is in the timeline lane and the answer is only
  // classified when the stream ends. Keep the placeholder up for that
  // whole stretch (not just before the first trace block) so the bubble
  // never sits blank while the model works.
  const showThinkingPlaceholder =
    isStreamingThis &&
    !hasTextContent &&
    ((isAuditTimelineEnabled() && routesNarration(chatType)) ||
      !hasTraceBlocks);

  /**
   * The trace pill's label: one word, beside "Graph".
   *
   * It used to carry its own tallies — "Audit log · 4 steps · 21 evidence" —
   * which put a number in front of the reader before they had asked for one,
   * and made a control that opens a panel read like a result. The counts are
   * not lost: they are what the panel itself shows.
   */
  const traceButtonLabel = "Audit";

  // Cycle through thinking messages every 5s while the placeholder is visible
  const [thinkingMessage, setThinkingMessage] = useState(() =>
    pickThinkingMessage(),
  );
  useEffect(() => {
    if (!showThinkingPlaceholder) return;
    const interval = setInterval(() => {
      setThinkingMessage(pickThinkingMessage());
    }, 5000);
    return () => clearInterval(interval);
  }, [showThinkingPlaceholder]);

  // block_id of the *last* MODIFIED_WORKBOOK block. A single assistant turn
  // may invoke the workbook tools multiple times — once for inspection,
  // once for the real edit — and each call emits its own download card
  // during the live SSE stream. The cache-overlay on the backend ensures
  // call N's output already contains everything from calls 1..N-1, so only
  // the last card represents the cumulative state. Earlier cards are stale
  // snapshots that would download the wrong file if clicked. Hide them at
  // render time so live and replay match.
  //
  // Keyed by block_id, NOT positional index: the render loop maps over
  // `answerBlocks`, which the audit timeline filters (narration text blocks
  // removed), so raw-array indexes do not survive into it — an index-based
  // guard silently suppressed every card whenever the timeline flag was on.
  const lastModifiedWorkbookId = useMemo(() => {
    let id: string | null = null;
    for (const b of msg.content_blocks) {
      if (
        b.type === BlockType.MODIFIED_WORKBOOK &&
        !(b as ModifiedWorkbookBlock).hidden
      ) {
        id = (b as ModifiedWorkbookBlock).block_id;
      }
    }
    return id;
  }, [msg.content_blocks]);

  // The schema-graph (Map/SQL) block is rendered once, after all the answer
  // markdown, rather than in its natural streaming position — otherwise the
  // buttons land in the middle of the schema mapping tables.
  const lastSchemaGraphBlock = useMemo(() => {
    for (let i = msg.content_blocks.length - 1; i >= 0; i--) {
      const b = msg.content_blocks[i];
      if (b.type === BlockType.SCHEMA_GRAPH) {
        return b as SchemaGraphBlock;
      }
    }
    return null;
  }, [msg.content_blocks]);

  // Parse table data from markdown text blocks for Excel export (Univer mode).
  // Markdown tables now render as plain HTML <table> elements; the
  // canonical workbook view is the artifact card (ModifiedWorkbookBlock),
  // which already shows a faithful preview + a real download. No
  // Univer interception, no second-rendering of the same data.
  useEffect(() => {
    setHasTables(containerHasTables(contentRef.current));
  }, [msg.content_blocks, isStreamingThis]);

  // Index chart blocks by id and pre-scan text blocks for
  // `[[chart:<id>]]` reference tokens so we can render referenced
  // charts inline at the token position, suppress them from their
  // natural streaming position (which would otherwise put all
  // charts above the prose), and append unreferenced charts at the
  // end as a fallback. The title set is used by the markdown `img`
  // override to swallow hallucinated `![title](...)` chart refs.
  const { chartBlocksById, referencedChartIds, chartTitlesLower } =
    useMemo(() => {
      const byId = new Map<string, ChartBlockType>();
      const titles = new Set<string>();
      for (const b of msg.content_blocks) {
        if (b.type === BlockType.CHART) {
          const cb = b as ChartBlockType;
          if (cb.chart_id) byId.set(cb.chart_id, cb);
          if (cb.title) titles.add(cb.title.trim().toLowerCase());
        }
      }
      const referenced = new Set<string>();
      const tokenRe = /\[\[chart:([A-Za-z0-9_-]+)\]\]/g;
      for (const b of msg.content_blocks) {
        if (b.type === BlockType.TEXT) {
          const t = (b as TextBlock).text ?? "";
          for (const m of t.matchAll(tokenRe)) {
            referenced.add(m[1]);
          }
        }
      }
      return {
        chartBlocksById: byId,
        referencedChartIds: referenced,
        chartTitlesLower: titles,
      };
    }, [msg.content_blocks]);

  // A fenced block a hosting surface claims (DOP-618) draws its own way;
  // every other block is code, as before.
  const codeRenderers = useCodeBlockRenderers();
  // Every claimed block of the answer, by language, so a surface can act on
  // an answer's blocks together (DOP-653). Empty where nothing is claimed.
  const claimedBlocks = useMemo(() => {
    const languages = Object.keys(codeRenderers);
    if (languages.length === 0) return {} as Record<string, string[]>;
    const text = msg.content_blocks
      .filter((b) => b.type === BlockType.TEXT)
      .map((b) => (b as TextBlock).text ?? "")
      .join("\n");
    return Object.fromEntries(
      languages.map((language) => [language, fencedBlocks(text, language)]),
    );
  }, [codeRenderers, msg.content_blocks]);
  const claimedPre = useCallback(
    (props: React.HTMLAttributes<HTMLPreElement> & { node?: unknown }) => {
      const { node: _node, children, ...rest } = props;
      const child = React.Children.toArray(children)[0];
      if (
        React.isValidElement<{
          className?: string;
          children?: React.ReactNode;
        }>(child)
      ) {
        const language = /language-([\w-]+)/.exec(
          child.props.className ?? "",
        )?.[1];
        const render = language ? codeRenderers[language] : undefined;
        if (render) {
          const code = String(child.props.children ?? "").replace(/\n$/, "");
          return (
            <>
              {render(code, {
                streaming: isStreamingThis,
                siblings: (language && claimedBlocks[language]) || [],
              })}
            </>
          );
        }
      }
      return <pre {...rest}>{children}</pre>;
    },
    [codeRenderers, isStreamingThis, claimedBlocks],
  );

  const markdownComponents = useMemo(
    () =>
      isStreamingThis
        ? {
            pre: claimedPre,
            table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
              <MarkdownTable {...props} />
            ),
            td: StatusCell,
            img: (
              props: React.ImgHTMLAttributes<HTMLImageElement> & {
                node?: unknown;
              },
            ) => {
              const { node: _node, alt, ...rest } = props;
              const normalizedAlt = (alt ?? "").trim().toLowerCase();
              // Swallow hallucinated `![title](...)` refs that
              // match a known chart title — the chart renders
              // separately. Otherwise pass through.
              if (normalizedAlt && chartTitlesLower.has(normalizedAlt)) {
                return null;
              }
              return <img {...rest} alt={alt ?? ""} />;
            },
          }
        : {
            pre: claimedPre,
            table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
              <MarkdownTable {...props} />
            ),
            td: StatusCell,
            img: (
              props: React.ImgHTMLAttributes<HTMLImageElement> & {
                node?: unknown;
              },
            ) => {
              const { node: _node, alt, ...rest } = props;
              const normalizedAlt = (alt ?? "").trim().toLowerCase();
              if (normalizedAlt && chartTitlesLower.has(normalizedAlt)) {
                return null;
              }
              return <img {...rest} alt={alt ?? ""} />;
            },
            a: (
              props: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
                node?: unknown;
              },
            ) => {
              const { href, children, node: _node, ...rest } = props;
              // A citation anchor is a cited PHRASE, not a link out.
              // Clicking it puts the matching card in the audit trail and
              // leaves the reader where they are; navigation to the document
              // lives on that card. Only when no chain is mounted (exports,
              // the shared view) does a citation fall back to opening
              // directly — see `useCitationChain`.
              const citation = citationChain
                ? resolveCitation(href, citationEvents)
                : null;
              if (citation) {
                return (
                  <CitationAnchor citation={citation} href={href}>
                    {children}
                  </CitationAnchor>
                );
              }
              if (isDopplerUri(href)) {
                const handleClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  if (!href) return;
                  // Excel citations open the in-app workbook viewer (scrolls
                  // to and highlights the cited cell); PDF citations open the
                  // in-app document viewer at the cited page. Anything else —
                  // and any case where a viewer isn't mounted — falls back to
                  // opening in a new tab. See `useDopplerUriOpener`.
                  openDopplerHref(href, { chatId });
                };
                return (
                  <a
                    {...rest}
                    href={href}
                    onClick={handleClick}
                    className="markdown-doppler-link"
                  >
                    {children}
                  </a>
                );
              }
              if (href?.startsWith("/api/")) {
                const apiBase = getApiBaseURLFromEnv() ?? "";
                const token = getSessionToken();
                const isSignedUrl = href.includes("/signed-url");
                const handleClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  const fullUrl = `${apiBase}${href}`;
                  fetch(fullUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  })
                    .then((res) => {
                      if (!res.ok)
                        throw new Error(`Request failed: ${res.status}`);
                      if (isSignedUrl) {
                        return res
                          .json()
                          .then((data: { signed_url?: string }) => {
                            if (data.signed_url) {
                              window.open(
                                data.signed_url,
                                "_blank",
                                "noopener",
                              );
                            }
                          });
                      }
                      return res.blob().then((blob) => {
                        const filename = href.includes("/download/")
                          ? "sheet_data.csv"
                          : "download";
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = filename;
                        a.click();
                        URL.revokeObjectURL(url);
                      });
                    })
                    .catch((err) => console.error("File open failed:", err));
                };
                return (
                  <a
                    {...rest}
                    href={href}
                    onClick={handleClick}
                    className={isSignedUrl ? "markdown-api-link" : undefined}
                  >
                    {children}
                  </a>
                );
              }
              return (
                <a href={href} {...rest}>
                  {children}
                </a>
              );
            },
          },
    [
      isStreamingThis,
      claimedPre,
      chatId,
      chartTitlesLower,
      openDopplerHref,
      citationChain,
      citationEvents,
    ],
  );

  const renderTextWithInlineCharts = (text: string, baseKey: string) => {
    const tokenRe = /\[\[chart:([A-Za-z0-9_-]+)\]\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let i = 0;
    let m: RegExpExecArray | null;
    const pushMarkdown = (segment: string, key: string) => {
      if (!segment.trim()) return;
      parts.push(
        <MarkdownErrorBoundary
          key={key}
          fallback={<div className="whitespace-pre-wrap">{segment}</div>}
        >
          <ReactMarkdown
            remarkPlugins={[
              remarkGfm,
              [remarkMath, { singleDollarTextMath: false }],
            ]}
            rehypePlugins={[[rehypeKatex, katexOptions]]}
            urlTransform={dopplerUrlTransform}
            components={markdownComponents}
          >
            {normalizeMathDelimiters(segment)}
          </ReactMarkdown>
        </MarkdownErrorBoundary>,
      );
    };
    while ((m = tokenRe.exec(text)) !== null) {
      pushMarkdown(text.slice(lastIndex, m.index), `${baseKey}-md-${i}`);
      const cid = m[1];
      const cb = chartBlocksById.get(cid);
      if (cb) {
        parts.push(
          <Suspense
            key={`${baseKey}-chart-${cid}-${i}`}
            fallback={
              <div
                data-chart-block={cb.chart_id ?? cid}
                className="h-[300px] bg-gray-50 rounded-lg animate-pulse my-3"
              />
            }
          >
            <EChartsBlock
              chartData={cb.chart_data}
              chartType={cb.chart_type}
              title={cb.title}
              version={cb.version}
              chartId={cb.chart_id ?? cid}
            />
          </Suspense>,
        );
      }
      // If the matching chart block hasn't streamed yet, skip
      // silently — once it arrives the next render fills it in.
      lastIndex = tokenRe.lastIndex;
      i++;
    }
    pushMarkdown(text.slice(lastIndex), `${baseKey}-md-tail`);
    if (parts.length === 0) {
      // All-whitespace text — render an empty paragraph spacer so
      // surrounding layout doesn't collapse.
      pushMarkdown(text, `${baseKey}-md-empty`);
    }
    return parts;
  };

  useEffect(() => {
    if (!exportError) return;
    const timer = setTimeout(() => setExportError(null), 4000);
    return () => clearTimeout(timer);
  }, [exportError]);

  const handleExcelExport = () => {
    try {
      if (contentRef.current && containerHasTables(contentRef.current)) {
        exportMessageTablesToExcel(contentRef.current, msg.id);
      }
    } catch (err) {
      console.error("Excel export failed:", err);
      setExportError("Excel export failed. Please try again.");
    }
  };

  const handlePdfExport = async () => {
    if (!contentRef.current || !chatId) return;
    setIsExportingPdf(true);
    setExportError(null);
    try {
      const tmp = document.createElement("div");
      tmp.innerHTML = contentRef.current.innerHTML;

      // Markdown tables now render as plain <table> — no hidden
      // PDF-shadow table or Univer wrapper to clean up.

      // Replace mounted chart containers with rendered images from block data.
      // Charts may be unmounted (IntersectionObserver), so we render from
      // persisted chart_data. Use data-chart-block="<id>" for stable lookup.
      const { renderChartToImage } = await import("../utils/chartExport");
      const chartBlocksById = new Map<string, ChartBlockType>();
      const chartBlocksOrdered: ChartBlockType[] = [];
      for (const block of msg.content_blocks) {
        if (block.type === BlockType.CHART) {
          const cb = block as ChartBlockType;
          chartBlocksOrdered.push(cb);
          if (cb.chart_id) chartBlocksById.set(cb.chart_id, cb);
        }
      }
      const chartPlaceholders = tmp.querySelectorAll("[data-chart-block]");
      let positionalIdx = 0;
      chartPlaceholders.forEach((placeholder) => {
        const id = placeholder.getAttribute("data-chart-block") ?? "";
        // Stable lookup by chart_id; fall back to positional for blocks without IDs
        const chartBlock =
          (id && chartBlocksById.get(id)) ||
          chartBlocksOrdered[positionalIdx++];
        if (!chartBlock) {
          placeholder.remove();
          return;
        }
        const dataUrl = renderChartToImage(
          chartBlock.chart_data,
          chartBlock.chart_type,
          chartBlock.title,
        );
        if (dataUrl) {
          const img = document.createElement("img");
          img.src = dataUrl;
          img.style.maxWidth = "100%";
          img.style.margin = "12px 0";
          placeholder.replaceWith(img);
        } else {
          placeholder.remove();
        }
      });

      const overflowChartPlaceholders = tmp.querySelectorAll(
        "[data-chart-artifact-block]",
      );
      for (const placeholder of overflowChartPlaceholders) {
        const rawArtifactId =
          placeholder.getAttribute("data-chart-artifact-block") ?? "";
        const artifactId = Number(rawArtifactId);
        if (!Number.isInteger(artifactId) || artifactId <= 0) {
          throw new Error("Invalid overflow chart artifact id");
        }
        const { blob } = await fetchArtifactBlob(artifactId);
        const artifactPayload = JSON.parse(await blob.text()) as {
          chart_data?: Record<string, unknown>;
          chart_type?: string;
          title?: string;
        };
        if (
          !artifactPayload.chart_data ||
          typeof artifactPayload.chart_type !== "string"
        ) {
          throw new Error("Overflow chart artifact payload is invalid");
        }
        const dataUrl = renderChartToImage(
          artifactPayload.chart_data,
          artifactPayload.chart_type,
          artifactPayload.title,
        );
        if (!dataUrl) {
          throw new Error("Overflow chart export failed");
        }
        const img = document.createElement("img");
        img.src = dataUrl;
        img.style.maxWidth = "100%";
        img.style.margin = "12px 0";
        placeholder.replaceWith(img);
      }

      await exportMessageToPdf(tmp.innerHTML, chatId, msg.id);
    } catch (err) {
      console.error("PDF export failed:", err);
      setExportError("PDF export failed. Please try again.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const fetchArtifactBlob = async (
    artifactId: number,
  ): Promise<{ blob: Blob; filename: string | null }> => {
    if (!chatId) {
      throw new Error("Chat id missing");
    }
    const response = await apiFetch(
      `/api/v3/chats/${chatId}/artifacts/${artifactId}/download`,
      { method: "GET" },
    );
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    return {
      blob: await response.blob(),
      filename: parseFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
      ),
    };
  };

  const handleArtifactDownload = async (
    artifactId: number,
    fallbackFilename: string,
  ) => {
    if (!chatId) return;
    setLoadingArtifactId(artifactId);
    setExportError(null);
    try {
      const { blob, filename } = await fetchArtifactBlob(artifactId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename ?? fallbackFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Artifact download failed:", err);
      setExportError("Download failed. Please try again.");
    } finally {
      setLoadingArtifactId(null);
    }
  };

  return (
    <div className="flex flex-col items-start p-0 gap-[9px] answer-column">
      {/* The two ways into the reasoning behind this answer, side by side:
			    the steps that produced it and the data layer it walked. The row
			    is conditional, not just its contents — an empty flex box would
			    still spend the answer column's 9px gap. */}
      {(showTracePill || contextGraphHref) && (
        <div className="flex flex-wrap items-center gap-2">
          {showTracePill && (
            <button
              className={`thought-for-steps-btn${
                openView === "audit" ? " is-open" : ""
              }`}
              onClick={() => onOpenTrace?.(msg.id)}
              disabled={!onOpenTrace}
              aria-pressed={openView === "audit"}
            >
              <span>{traceButtonLabel}</span>
              <ChevronRight
                className="thought-for-steps-chevron"
                aria-hidden="true"
              />
            </button>
          )}
          {contextGraphHref && (
            <button
              type="button"
              className={`thought-for-steps-btn${
                openView === "graph" ? " is-open" : ""
              }`}
              aria-pressed={openView === "graph"}
              // A host that can show the graph itself does; the chat
              // route has nowhere to put it and leaves for the page.
              onClick={() =>
                onOpenGraph ? onOpenGraph(msg.id) : navigate(contextGraphHref)
              }
            >
              <span>Graph</span>
              <ChevronRight
                className="thought-for-steps-chevron"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )}
      {coverageWarningText && (
        <button
          type="button"
          className="citation-coverage-warning"
          onClick={() => onOpenTrace?.(msg.id)}
          disabled={!onOpenTrace}
        >
          <AlertTriangle aria-hidden="true" />
          <span>{coverageWarningText}</span>
        </button>
      )}
      {/*
       * The body scale of an answer; the lead paragraph steps up to 16px
       * from `MessageList.css`. `prose prose-gray` is gone: DOP-304 put an
       * explicit px type scale on `.markdown-content`, and Tailwind
       * Typography's em-derived one sat underneath it setting a second
       * size for code, tables and captions.
       */}
      <div
        ref={contentRef}
        data-export-answer
        className="markdown-content answer-column font-normal text-[15px] leading-[1.62] text-text-body flex-none order-0 flex-grow-0 overflow-x-auto"
        style={{ fontFamily: '"Inter", "Inter Display", sans-serif' }}
      >
        {showThinkingPlaceholder ? (
          <span className="thinking-placeholder">
            {thinkingMessage}
            <span className="thinking-ellipsis" aria-hidden="true" />
          </span>
        ) : (
          <>
            {answerBlocks.map((block, index) => {
              if (block.type === BlockType.THINKING) {
                return (
                  <ThinkingContent
                    key={`thinking-${index}`}
                    isStreaming={isStreamingThis}
                    text={block.text}
                  />
                );
              }
              if (block.type === BlockType.CHART) {
                // Suppress charts in their natural streaming
                // position. Referenced charts render inline via
                // `renderTextWithInlineCharts`; unreferenced
                // charts are appended after the block map as a
                // fallback. This avoids the "all charts at the
                // top, then text streams in below" UX.
                return null;
              }
              if (block.type === BlockType.TABLE_OVERFLOW_PENDING) {
                const tableBlock = block as TableOverflowPendingBlock;
                return (
                  <div
                    key={`table-overflow-pending-${tableBlock.block_id}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4 my-3"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      Preparing table download
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {tableBlock.row_count} rows will be available as{" "}
                      {tableBlock.filename}.
                    </div>
                  </div>
                );
              }
              if (block.type === BlockType.TABLE_OVERFLOW) {
                const tableBlock = block as TableOverflowBlock;
                return (
                  <div
                    key={`table-overflow-${tableBlock.block_id}`}
                    className="rounded-lg border border-gray-200 bg-white p-4 my-3 flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Large table available for download
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {tableBlock.row_count} rows • {tableBlock.filename}
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        handleArtifactDownload(
                          tableBlock.artifact_id,
                          tableBlock.filename,
                        )
                      }
                      disabled={loadingArtifactId === tableBlock.artifact_id}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Download className="w-4 h-4" />
                      {loadingArtifactId === tableBlock.artifact_id
                        ? "Opening..."
                        : "Download CSV"}
                    </button>
                  </div>
                );
              }
              if (block.type === BlockType.CHART_OVERFLOW_PENDING) {
                const chartBlock = block as ChartOverflowPendingBlock;
                return (
                  <div
                    key={`chart-overflow-pending-${chartBlock.block_id}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4 my-3"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      Preparing chart download
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {chartBlock.title ?? "Chart"} exceeds the inline size
                      limit and will be available as {chartBlock.filename}.
                    </div>
                  </div>
                );
              }
              if (block.type === BlockType.CHART_OVERFLOW) {
                const chartBlock = block as ChartOverflowBlock;
                return (
                  <div
                    key={`chart-overflow-${chartBlock.block_id}`}
                    data-chart-artifact-block={chartBlock.artifact_id}
                    className="rounded-lg border border-gray-200 bg-white p-4 my-3 flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Chart available for download
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {chartBlock.title ?? chartBlock.chart_type} •{" "}
                        {formatBytes(chartBlock.size_bytes)} •{" "}
                        {chartBlock.filename}
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        handleArtifactDownload(
                          chartBlock.artifact_id,
                          chartBlock.filename,
                        )
                      }
                      disabled={loadingArtifactId === chartBlock.artifact_id}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Download className="w-4 h-4" />
                      {loadingArtifactId === chartBlock.artifact_id
                        ? "Opening..."
                        : "Download JSON"}
                    </button>
                  </div>
                );
              }
              if (block.type === BlockType.MODIFIED_WORKBOOK) {
                const wbBlock = block as ModifiedWorkbookBlock;
                // ``hidden`` blocks (inline-mode delivery) keep the
                // durable storage URI for export bundling but
                // suppress the chat card. Skip them in render.
                if (wbBlock.hidden) return null;
                // Only render the *last* workbook card per turn —
                // see ``lastModifiedWorkbookId`` for rationale.
                if (wbBlock.block_id !== lastModifiedWorkbookId) return null;
                return (
                  <ModifiedWorkbookBlockView
                    key={`modified-workbook-${wbBlock.block_id}`}
                    block={wbBlock}
                    chatId={chatId}
                  />
                );
              }
              if (block.type === BlockType.SCHEMA_GRAPH) {
                // Suppress in the natural streaming position — the
                // Map/SQL buttons render once, after all the answer
                // markdown, via `lastSchemaGraphIdx` below.
                return null;
              }
              if (block.type === BlockType.LOAN_TAPE_PREVIEW) {
                const preview = block as LoanTapePreviewBlock;
                return (
                  <LoanTapePreviewBlockView
                    key={`loan-tape-preview-${index}`}
                    columns={preview.columns}
                    rows={preview.rows}
                    onInspectColumn={onInspectColumn}
                  />
                );
              }
              if (block.type === BlockType.CITED_TABLE_ARTIFACT) {
                const citedBlock = block as CitedTableArtifactBlock;
                const render = resolveCitedTableRender(
                  citedBlock,
                  isCitationArtifactsEnabled(),
                );
                if (render.kind === "typed") {
                  return (
                    <CitedTableArtifactView
                      key={`cited-table-${citedBlock.artifact_id}`}
                      artifact={render.artifact}
                      chatId={chatId}
                    />
                  );
                }
                // Typed view unavailable (flag off, or the artifact failed
                // validation). Keyed-only delivery already stripped the table
                // from the message text, so the backend's projection is the only
                // remaining copy of the data — render it rather than nothing.
                if (render.kind === "none") return null;
                return (
                  <div key={`cited-table-fallback-${citedBlock.artifact_id}`}>
                    {renderTextWithInlineCharts(
                      render.markdown,
                      `cited-table-fallback-${citedBlock.artifact_id}`,
                    )}
                  </div>
                );
              }
              if (
                block.type === BlockType.CITATION_HARNESS_REPORT &&
                isCitationHarnessEnabled()
              ) {
                const harnessBlock = block as CitationHarnessReportBlock;
                return (
                  <CitationHarnessReportView
                    key={`citation-harness-${harnessBlock.block_id}`}
                    title={harnessBlock.title}
                    report={harnessBlock.report}
                  />
                );
              }
              if (block.type !== BlockType.TEXT) return null;
              return (
                <React.Fragment key={index}>
                  {renderTextWithInlineCharts(block.text, `text-${index}`)}
                </React.Fragment>
              );
            })}
            {lastSchemaGraphBlock && (
              <SchemaMappingBlockView
                title={lastSchemaGraphBlock.title}
                nodes={lastSchemaGraphBlock.nodes}
                edges={lastSchemaGraphBlock.edges}
                sql={lastSchemaGraphBlock.sql}
              />
            )}
            {!isStreamingThis &&
              msg.content_blocks
                .filter((b) => b.type === BlockType.CHART)
                .map((b) => b as ChartBlockType)
                .filter(
                  (cb) => !cb.chart_id || !referencedChartIds.has(cb.chart_id),
                )
                .map((cb, i) => (
                  <Suspense
                    key={`chart-trailing-${cb.chart_id ?? i}`}
                    fallback={
                      <div
                        data-chart-block={cb.chart_id ?? `trailing-${i}`}
                        className="h-[300px] bg-gray-50 rounded-lg animate-pulse my-3"
                      />
                    }
                  >
                    <EChartsBlock
                      chartData={cb.chart_data}
                      chartType={cb.chart_type}
                      title={cb.title}
                      version={cb.version}
                      chartId={cb.chart_id ?? `trailing-${i}`}
                    />
                  </Suspense>
                ))}
            {isStreamingThis && !hasTraceBlocks && (
              <span className="inline-block w-2 h-4 bg-black animate-pulse" />
            )}
          </>
        )}
      </div>
      {msg.failed && !isStreamingThis && (
        // The pipeline did not produce an answer. One plain sentence in
        // red, and the way to try again right beside it; the technical
        // reason stays in the tooltip rather than in the user's face.
        <div
          role="alert"
          data-testid="assistant-failed-notice"
          title={msg.failed.message}
          className="flex-none order-1 flex items-center gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800"
        >
          <span>{FAILED_ANSWER_NOTICE}</span>
          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-1 rounded border border-red-300 bg-white px-2 py-0.5 text-xs font-semibold text-red-800 hover:bg-red-100"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Retry
            </button>
          )}
        </div>
      )}
      <div className="msg-actions flex flex-row items-center p-0 gap-[14px] h-5 flex-none order-1 flex-grow-0">
        <button
          onClick={() => onCopy(extractText(msg), msg.id)}
          className="w-5 h-5 flex-none flex-grow-0 text-gray-600 hover:text-gray-800 transition-colors relative"
          data-tip={copiedMessageId === msg.id ? "Copied!" : "Copy message"}
          aria-label="Copy message"
        >
          {copiedMessageId === msg.id ? (
            <Check className="w-5 h-5 text-green-600" />
          ) : (
            <Copy className="w-5 h-5" />
          )}
        </button>
        <button
          onClick={handlePdfExport}
          disabled={isStreamingThis || isExportingPdf || !chatId}
          className="w-5 h-5 flex-none flex-grow-0 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-tip="Export as PDF"
          aria-label="Export as PDF"
        >
          <Download
            className={`w-5 h-5 ${isExportingPdf ? "animate-pulse" : ""}`}
          />
        </button>
        {hasTables && lastModifiedWorkbookId === null && (
          <button
            onClick={handleExcelExport}
            disabled={isStreamingThis}
            className="w-5 h-5 flex-none flex-grow-0 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-tip="Export tables to Excel"
            aria-label="Export tables to Excel"
          >
            <FileSpreadsheet className="w-5 h-5" />
          </button>
        )}
        <button
          type="button"
          className="w-5 h-5 flex-none flex-grow-0 text-gray-600 hover:text-gray-800 transition-colors"
          data-tip="Good response"
          aria-label="Good response"
        >
          <ThumbsUp className="w-5 h-5" />
        </button>
        <button
          type="button"
          className="w-5 h-5 flex-none flex-grow-0 text-gray-600 hover:text-gray-800 transition-colors"
          data-tip="Bad response"
          aria-label="Bad response"
        >
          <ThumbsDown className="w-5 h-5" />
        </button>
        {/* Last in the row, the attempt count right after it: "↻ ‹ 2 / 2 ›". */}
        {onRegenerate && !isStreamingThis && (
          <button
            type="button"
            onClick={onRegenerate}
            className="w-5 h-5 flex-none flex-grow-0 text-gray-600 hover:text-gray-800 transition-colors"
            data-tip="Retry: regenerate this answer"
            aria-label="Retry"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        )}
        {msg.versions && msg.versions.total > 1 && !isStreamingThis && (
          <AnswerVersionSwitcher
            versions={msg.versions}
            selectedVersionId={selectedVersionId}
            onSelectVersion={onSelectVersion}
          />
        )}
      </div>
      {exportError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-1.5 flex-none order-2">
          {exportError}
        </div>
      )}
    </div>
  );
};

export default AssistantMessage;
